import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { FiSearch, FiX } from "react-icons/fi";
import type {
  CatalogEvent,
  CatalogEventNotifier,
} from "@/features/catalog/mediators/catalogEvents";
import { isCatalogEvent } from "@/features/catalog/mediators/catalogEvents";
import {
  type CatalogSearchEffect,
  type CatalogSearchState,
  createCatalogSearchMediator,
  isCatalogSearchEvent,
} from "@/features/catalog/mediators/catalogSearchMediator";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import { MAX_SEARCH_QUERY_LENGTH } from "@/features/catalog/utils/catalogSearch";
import { SearchScopeSelect } from "@/features/search/components/SearchScopeSelect";
import type { SearchScope } from "@/features/search/searchScope";
import { cn } from "@/shared/lib";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  getHierarchyResourceSnapshot,
  useComponentNode,
  useHierarchyResource,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import { Input } from "@/shared/ui/form";

type SearchBarProps = { readonly className?: string };

export type SearchBarViewProps = {
  readonly className?: string;
  readonly query: string;
  readonly scope: SearchScope;
  readonly onEvent: CatalogEventNotifier;
};

export const SearchBar: React.FunctionComponent<SearchBarProps> = ({
  className,
}: SearchBarProps) => {
  const searchQuery = useCatalogStore((state) => state.searchQuery);
  const searchScope = useCatalogStore((state) => state.searchScope);
  const setSearchQuery = useCatalogStore((state) => state.setSearchQuery);
  const setSearchScope = useCatalogStore((state) => state.setSearchScope);
  const navigate = useNavigate();
  const effects = useRef<(effect: CatalogSearchEffect) => void>(
    (): void => undefined,
  );
  effects.current = (effect: CatalogSearchEffect): void => {
    switch (effect.type) {
      case "set-query":
        setSearchQuery(effect.query);
        return;
      case "set-scope":
        setSearchScope(effect.scope);
        return;
      case "navigate":
        void navigate(effect.destination);
    }
  };
  const mediator = useHierarchyResource<
    ReturnType<typeof createCatalogSearchMediator>
  >((previous) =>
    createCatalogSearchMediator(
      { query: searchQuery, scope: searchScope },
      (effect) => effects.current(effect),
      previous?.isDisposed
        ? getHierarchyResourceSnapshot<CatalogSearchState>(previous)
        : undefined,
    ),
  );
  useEffect(() => {
    mediator.dispatch({
      type: "catalog/search-synchronized",
      query: searchQuery,
      scope: searchScope,
    });
  }, [mediator, searchQuery, searchScope]);
  const node = useComponentNode((event) => {
    if (!isCatalogEvent(event) || !isCatalogSearchEvent(event)) {
      return "unhandled";
    }
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const state = useMediatorSnapshot(mediator);
  const notify: CatalogEventNotifier = (
    event: CatalogEvent,
  ): ReturnType<CatalogEventNotifier> => node.dispatch(event).status;

  return (
    <ComponentScope node={node}>
      <SearchBarView
        className={className}
        query={state.query}
        scope={state.scope}
        onEvent={notify}
      />
    </ComponentScope>
  );
};

export const SearchBarView: React.FunctionComponent<SearchBarViewProps> = ({
  className,
  query,
  scope,
  onEvent,
}: SearchBarViewProps) => (
  <search aria-label="投稿検索" className={cn("w-full md:w-auto", className)}>
    <form
      onSubmit={(event: React.SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        onEvent({ type: "catalog/search-submitted" });
      }}
      className="catalog-search-form flex w-full items-center gap-2"
    >
      <SearchScopeSelect
        ariaLabel="投稿検索の検索モード"
        scope={scope}
        onChange={(nextScope: SearchScope): void => {
          onEvent({ type: "catalog/search-scope-changed", scope: nextScope });
        }}
      />
      <div
        className="catalog-search-field relative flex min-w-0 flex-1 items-center"
        data-has-query={query ? "true" : "false"}
      >
        <Input
          type="text"
          value={query}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
            onEvent({
              type: "catalog/search-query-changed",
              query: event.currentTarget.value,
            });
          }}
          placeholder="投稿検索"
          aria-label="投稿検索"
          maxLength={MAX_SEARCH_QUERY_LENGTH}
          className={`catalog-search-input min-h-11 min-w-0 flex-1 ${
            query ? "pr-24" : "pr-12"
          }`}
        />
        <fieldset
          aria-label="検索操作"
          className="absolute inset-y-0 right-0 m-0 flex min-w-0 items-center border-0 p-0"
        >
          {query && (
            <button
              type="button"
              onClick={(): void => {
                onEvent({ type: "catalog/search-cleared" });
              }}
              className="catalog-search-action flex size-11 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              aria-label="検索クリア"
            >
              <FiX size={18} aria-hidden="true" />
            </button>
          )}
          <button
            type="submit"
            disabled={!query.trim()}
            className={cn(
              "catalog-search-action flex size-11 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50",
              query && "catalog-search-action-divider",
            )}
            aria-label="検索"
          >
            <FiSearch size={18} aria-hidden="true" />
          </button>
        </fieldset>
      </div>
    </form>
  </search>
);
